import React, { useState } from 'react';
import LoadingState from './LoadingState';
import SearchFilterBar from './SearchFilterBar';
import ScoringGuidelines from './ScoringGuidelines';
import EvaluationTable from './EvaluationTable';
import SubmitAllButton from './SubmitAllButton';
import EmptyErrorState from './EmptyErrorState';
import CategorySelectionMessage from './CategorySelectionMessage';
import useCategoryJudgingData from './useCategoryJudgingData';
import useEvaluationScores from './useEvaluationScores';

import { Submission } from 'types/evaluation';

interface Judgment {
  overall_summary?: string;
  aspect_scores?: Array<{ value: number }>;
  documentId?: string;
}

interface EvaluationData {
  submissionId: string;
  scores: Record<string, number | string>;
  isRevision: boolean;
  existingJudgmentId: string | undefined;
}

interface FinalSubmissionsOverviewProps {
  submissions: Submission[];
  existingJudgments: Record<string, Judgment>;
  onCompleteAll: () => void;
  isCompletingAll: boolean;
  onEvaluationSubmit: (data: EvaluationData) => Promise<void>;
  error?: string;
  validUserCategories?: string[];
  onRetry?: () => void;
  year: number;
  isReadOnly?: boolean;
}

const FinalSubmissionsOverview: React.FC<FinalSubmissionsOverviewProps> = ({
  submissions,
  existingJudgments,
  onCompleteAll,
  isCompletingAll,
  onEvaluationSubmit,
  error,
  validUserCategories,
  onRetry,
  year,
  isReadOnly = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // State for collapsible scoring guidelines (open by default)
  const [guidelinesExpanded, setGuidelinesExpanded] = useState(true);

  // Get unique categories from submissions
  const availableCategories = [
    ...new Set(submissions.map(s => s.category)),
  ].sort();

  const {
    selectedCategory,
    setSelectedCategory,
    categoryJudgingData,
    loadingCriteria,
    initialLoading,
  } = useCategoryJudgingData(availableCategories, year);

  // Get judging data for current selected category
  const currentJudgingData = categoryJudgingData[selectedCategory];
  const aspects = currentJudgingData?.aspects || [];

  const {
    scores,
    saveStates,
    isSubmitting,
    handleScoreChange,
    handleSubmissionSave,
  } = useEvaluationScores(
    submissions,
    existingJudgments,
    categoryJudgingData,
    aspects,
    onEvaluationSubmit
  );

  // Get evaluation status for a submission
  const getEvaluationStatus = (submission: Submission) => {
    const submissionId = submission.documentId || submission.id;
    const judgment = submissionId ? existingJudgments[submissionId] : undefined;

    if (!judgment) {
      return { status: 'not-started', label: 'Not Started', color: 'gray' };
    }

    if (
      judgment.overall_summary === 'Draft Final' ||
      judgment.overall_summary === 'Draft'
    ) {
      return { status: 'draft', label: 'Draft Final', color: 'yellow' };
    }

    return { status: 'completed', label: 'Completed Final', color: 'green' };
  };

  // Filter submissions by selected category and search query
  const filteredSubmissions = submissions
    .filter(submission => {
      // Category filter
      const categoryMatch =
        !selectedCategory || submission.category === selectedCategory;

      // Search filter - search in project name, submitter name, organization, and pillar
      const searchMatch =
        searchQuery === '' ||
        [
          submission.description,
          submission.project_name,
          submission.title,
          submission.full_name,
          submission.pillar,
          submission.org_unit,
          submission.category,
        ].some(
          field =>
            field && field.toLowerCase().includes(searchQuery.toLowerCase())
        );

      return categoryMatch && searchMatch;
    })
    .sort((a, b) => {
      // Default sort by project name
      return (a.project_name || a.full_name || '').localeCompare(
        b.project_name || b.full_name || ''
      );
    });

  // Count submissions by status for current category
  const statusCounts = filteredSubmissions.reduce<Record<string, number>>(
    (acc, submission) => {
      const status = getEvaluationStatus(submission).status;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {}
  );

  const totalSubmissions = filteredSubmissions.length;
  const notStarted = statusCounts['not-started'] || 0;
  const drafts = statusCounts['draft'] || 0;
  const completed = statusCounts['completed'] || 0;

  // Check if all evaluations in current category are complete
  const allEvaluationsComplete = notStarted === 0;

  // Determine if individual or organization category
  const isIndividualCategory = (category: string): boolean => {
    return (
      category === 'Young Digital Leader Award' ||
      category === 'Women in Tech Award'
    );
  };

  // Show initial loading screen while prefetching all categories
  if (initialLoading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6">
      {/* 1st Container: Title, Filter, Search Bar */}
      <SearchFilterBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        availableCategories={availableCategories}
        submissions={submissions}
      />

      {/* 2nd Container: Scoring Guidelines */}
      <ScoringGuidelines
        selectedCategory={selectedCategory}
        guidelinesExpanded={guidelinesExpanded}
        onToggleGuidelines={() => setGuidelinesExpanded(!guidelinesExpanded)}
        loadingCriteria={loadingCriteria}
        aspects={aspects}
      />

      <CategorySelectionMessage
        selectedCategory={selectedCategory}
        availableCategoriesCount={availableCategories.length}
        error={error}
      />

      {/* 3rd Container: Evaluation Table */}
      <EvaluationTable
        selectedCategory={selectedCategory}
        filteredSubmissions={filteredSubmissions}
        aspects={aspects}
        loadingCriteria={loadingCriteria}
        scores={scores}
        saveStates={saveStates}
        isSubmitting={isSubmitting}
        existingJudgments={existingJudgments}
        isIndividualCategory={isIndividualCategory}
        getEvaluationStatus={getEvaluationStatus}
        handleScoreChange={handleScoreChange}
        handleSubmissionSave={handleSubmissionSave}
        isReadOnly={isReadOnly}
      />

      {/* Submit All Button - Bottom Right with better styling */}
      <SubmitAllButton
        selectedCategory={selectedCategory}
        filteredSubmissionsCount={filteredSubmissions.length}
        onCompleteAll={onCompleteAll}
        isCompletingAll={isCompletingAll}
        allEvaluationsComplete={allEvaluationsComplete}
      />

      {/* Empty State / Error State */}
      <EmptyErrorState
        availableCategoriesCount={availableCategories.length}
        error={error}
        validUserCategories={validUserCategories}
        onRetry={onRetry}
      />
    </div>
  );
};

export default FinalSubmissionsOverview;
